import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Check, RotateCcw, Loader2, Sparkles, ChevronDown, FilePlus, Wand2, CheckCircle, MessageSquare, Globe, BookOpen } from 'lucide-react';
import apiService from '../../services/api';
import { useProjectStore } from '../../store';
import {
  InstructionAssistantResponse,
  SuggestedSchema,
  SuggestedTool,
  Model,
} from '../../types';

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

type IntentType = 'create' | 'enhance' | 'validate' | 'assistant';

const INTENT_OPTIONS: Array<{ value: IntentType; label: string; description: string; icon: React.ElementType }> = [
  { value: 'create', label: 'Creator', description: 'Generates new content from input', icon: FilePlus },
  { value: 'enhance', label: 'Enhancer', description: 'Improves existing content', icon: Wand2 },
  { value: 'validate', label: 'Validator', description: 'Checks content against criteria', icon: CheckCircle },
  { value: 'assistant', label: 'Assistant', description: 'Conversational chat agent', icon: MessageSquare },
];

interface InstructionAssistantPanelProps {
  agentName: string;
  currentInstructions: string;
  projectId?: number;
  stepIntent?: string;
  models?: Model[];
  outputModelId?: number;
  upstreamModelId?: number;
  attachedToolTypes?: string[];
  onAccept: (instructions: string) => void;
  onClose: () => void;
  onCreateSchema?: (schemas: SuggestedSchema[]) => void;
  onAttachTools?: (tools: SuggestedTool[]) => void;
}

const InstructionAssistantPanel: React.FC<InstructionAssistantPanelProps> = ({
  agentName,
  currentInstructions,
  projectId,
  stepIntent,
  models = [],
  outputModelId,
  upstreamModelId,
  attachedToolTypes = [],
  onAccept,
  onClose,
  onCreateSchema,
  onAttachTools,
}) => {
  const [selectedModel, setSelectedModel] = useState<string>('auto');
  const [selectedIntent, setSelectedIntent] = useState<IntentType | null>(
    stepIntent && ['create', 'enhance', 'validate', 'assistant'].includes(stepIntent)
      ? (stepIntent as IntentType)
      : null
  );
  const [previewInstructions, setPreviewInstructions] = useState<string>('');
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [refinementInput, setRefinementInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelUsed, setModelUsed] = useState<string>('');
  const [hasGenerated, setHasGenerated] = useState(false);
  const [suggestedSchemas, setSuggestedSchemas] = useState<SuggestedSchema[] | null>(null);
  const [showSchemaPreview, setShowSchemaPreview] = useState(true);
  const [suggestedTools, setSuggestedTools] = useState<SuggestedTool[] | null>(null);
  const [showToolsPreview, setShowToolsPreview] = useState(true);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [selectedInputModelId, setSelectedInputModelId] = useState<number | undefined>(upstreamModelId);
  const [userOverrodeInputSchema, setUserOverrodeInputSchema] = useState(false);

  // Sync selectedInputModelId with upstreamModelId when it becomes available,
  // unless the user has already manually chosen a different one
  useEffect(() => {
    if (!userOverrodeInputSchema && upstreamModelId !== undefined && selectedInputModelId !== upstreamModelId) {
      setSelectedInputModelId(upstreamModelId);
    }
  }, [upstreamModelId, userOverrodeInputSchema]);

  const conversationEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  const hasExistingInstructions = currentInstructions.trim().length > 0;

  const getInputSchemaPayload = () => {
    if (!selectedInputModelId || models.length === 0) return undefined;
    const m = models.find((mod) => mod.model_id === selectedInputModelId);
    if (!m) return undefined;
    // Downstream API expects Record<string, string>. Reduce the rich
    // PropertyDefinition shape (AC-1251) back to its type string for this
    // consumer which only cares about property types.
    const stringProperties: Record<string, string> = {};
    for (const [key, value] of Object.entries(m.properties || {})) {
      stringProperties[key] =
        typeof value === 'string' ? value : String((value as any)?.type ?? 'unknown');
    }
    return { class_name: m.class_name, properties: stringProperties, description: m.description };
  };

  const { llmCatalog } = useProjectStore();
  const MODEL_OPTIONS = [
    { value: 'auto', label: 'Auto (recommended)' },
    ...llmCatalog.map((entry) => ({ value: entry.id, label: entry.label })),
  ];

  useEffect(() => {
    if (conversationEndRef.current && typeof conversationEndRef.current.scrollIntoView === 'function') {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  // Close model dropdown on click outside
  useEffect(() => {
    if (!showModelDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) {
        setShowModelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModelDropdown]);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: InstructionAssistantResponse = await apiService.generateInstructions({
        agent_name: agentName,
        project_id: projectId,
        model: selectedModel,
        intent: selectedIntent || undefined,
        input_schema: getInputSchemaPayload(),
      });
      setPreviewInstructions(response.instructions);
      setModelUsed(response.model_used);
      setHasGenerated(true);
      setSuggestedSchemas(response.suggested_schema || null);
      setShowSchemaPreview(true);
      setSuggestedTools(response.suggested_tools || null);
      setShowToolsPreview(true);
      setConversation([
        { role: 'user', content: `Generate instructions for "${agentName}"` },
        { role: 'assistant', content: response.instructions },
      ]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to generate instructions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImprove = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: InstructionAssistantResponse = await apiService.improveInstructions({
        agent_name: agentName,
        current_instructions: currentInstructions,
        project_id: projectId,
        model: selectedModel,
        intent: selectedIntent || undefined,
        input_schema: getInputSchemaPayload(),
      });
      setPreviewInstructions(response.instructions);
      setModelUsed(response.model_used);
      setHasGenerated(true);
      setSuggestedSchemas(response.suggested_schema || null);
      setShowSchemaPreview(true);
      setSuggestedTools(response.suggested_tools || null);
      setShowToolsPreview(true);
      const msgs: ConversationMessage[] = [
        { role: 'user', content: 'Improve my existing instructions' },
        { role: 'assistant', content: response.improvements_summary || response.instructions },
      ];
      setConversation(msgs);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to improve instructions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Start refine-with-feedback mode: preserve existing instructions, skip initial API call (AC-1202)
  const handleStartRefine = () => {
    setError(null);
    setPreviewInstructions(currentInstructions);
    setHasGenerated(true);
    setConversation([
      {
        role: 'assistant',
        content: "I've loaded your existing instructions. Tell me what you'd like to change.",
      },
    ]);
  };

  const handleRefine = async () => {
    if (!refinementInput.trim()) return;
    const userMessage = refinementInput.trim();
    setRefinementInput('');
    setIsLoading(true);
    setError(null);

    const updatedConversation: ConversationMessage[] = [
      ...conversation,
      { role: 'user', content: userMessage },
    ];
    setConversation(updatedConversation);

    try {
      const response: InstructionAssistantResponse = await apiService.refineInstructions({
        agent_name: agentName,
        current_instructions: previewInstructions,
        message: userMessage,
        conversation_history: updatedConversation.map((m) => ({
          role: m.role,
          content: m.content,
        })),
        project_id: projectId,
        model: selectedModel,
        intent: selectedIntent || undefined,
        input_schema: getInputSchemaPayload(),
      });
      setPreviewInstructions(response.instructions);
      setModelUsed(response.model_used);
      if (response.suggested_schema) {
        setSuggestedSchemas(response.suggested_schema);
        setShowSchemaPreview(true);
      }
      if (response.suggested_tools) {
        setSuggestedTools(response.suggested_tools);
        setShowToolsPreview(true);
      }
      setConversation((prev) => [
        ...prev,
        { role: 'assistant', content: response.message || 'Instructions updated.' },
      ]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to refine instructions. Please try again.');
      // Remove the optimistic user message on error
      setConversation((prev) => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleRefine();
    }
  };

  const handleAccept = () => {
    onAccept(previewInstructions);
  };

  return (
    <div className="border border-purple-200 dark:border-purple-800 rounded-lg bg-purple-50/50 dark:bg-purple-900/10 mt-3" data-testid="instruction-assistant-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-purple-200 dark:border-purple-800">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
            Instruction Assistant
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Model selector */}
          <div className="relative" ref={modelDropdownRef}>
            <button
              type="button"
              onClick={() => setShowModelDropdown(!showModelDropdown)}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              data-testid="model-selector"
            >
              {MODEL_OPTIONS.find((o) => o.value === selectedModel)?.label || 'Auto'}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showModelDropdown && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
                {MODEL_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSelectedModel(option.value);
                      setShowModelDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      selectedModel === option.value
                        ? 'text-purple-600 dark:text-purple-400 font-medium'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            data-testid="close-panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3">
        {/* Intent selection + action button */}
        {!hasGenerated && !isLoading && (
          <div className="flex flex-col gap-3 py-3">
            {/* Intent cards */}
            {!selectedIntent && (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  How will this agent be used?
                </p>
                <div className="grid grid-cols-2 gap-2" data-testid="intent-cards">
                  {INTENT_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSelectedIntent(opt.value)}
                        className="flex flex-col items-center gap-1 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-left"
                        data-testid={`intent-card-${opt.value}`}
                      >
                        <Icon className="w-5 h-5 text-purple-500" />
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{opt.label}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 text-center">{opt.description}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Intent selected - show input schema + generate/improve */}
            {selectedIntent && (
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span>Role: <span className="font-medium text-purple-600 dark:text-purple-400 capitalize">{selectedIntent}</span></span>
                  <button
                    type="button"
                    onClick={() => setSelectedIntent(null)}
                    className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
                    data-testid="change-intent"
                  >
                    Change
                  </button>
                </div>

                {/* Input schema dropdown (AC-1198) */}
                {models.length > 0 && (
                  <div className="w-full">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Input Schema <span className="text-gray-400">(optional)</span>
                    </label>
                    <select
                      value={selectedInputModelId || ''}
                      onChange={(e) => {
                        setSelectedInputModelId(e.target.value ? Number(e.target.value) : undefined);
                        setUserOverrodeInputSchema(true);
                      }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                      data-testid="input-schema-select"
                    >
                      <option value="">None</option>
                      {models.map((m) => (
                        <option key={m.model_id} value={m.model_id}>
                          {m.class_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {hasExistingInstructions ? (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      What would you like to do?
                    </p>
                    <div className="flex flex-col items-center gap-2 w-full">
                      <button
                        type="button"
                        onClick={handleStartRefine}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                        data-testid="refine-button"
                      >
                        <MessageSquare className="w-4 h-4" />
                        Refine with Feedback
                      </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400 text-center px-4">
                        Keep your instructions and describe specific changes
                      </span>
                      <button
                        type="button"
                        onClick={handleImprove}
                        className="mt-2 flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 underline"
                        data-testid="improve-button"
                      >
                        <Sparkles className="w-3 h-3" />
                        Or rewrite instructions from scratch
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      {`Generate instructions for "${agentName}"`}
                    </p>
                    <button
                      type="button"
                      onClick={handleGenerate}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                      data-testid="generate-button"
                    >
                      <Sparkles className="w-4 h-4" />
                      Generate Instructions
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Loading state */}
        {isLoading && !hasGenerated && (
          <div className="flex items-center justify-center gap-2 py-6">
            <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {hasExistingInstructions ? 'Improving...' : 'Generating...'}
            </span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Preview + conversation */}
        {hasGenerated && (
          <>
            {/* Instructions preview */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Preview
                </span>
                {modelUsed && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    Model: {modelUsed}
                  </span>
                )}
              </div>
              <div className="max-h-48 overflow-y-auto p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                {previewInstructions}
              </div>
            </div>

            {/* Suggested schema preview (AC-1199) */}
            {suggestedSchemas && suggestedSchemas.length > 0 && showSchemaPreview && (() => {
              // Determine the status of each suggested schema relative to the project's existing models
              const attachedOutputModel = models.find(m => m.model_id === outputModelId);
              const schemaStatuses = suggestedSchemas.map((schema, idx) => {
                const isTopLevel = idx === suggestedSchemas.length - 1;
                const existing = models.find(m => m.class_name === schema.class_name);
                const isAttached = isTopLevel && attachedOutputModel?.class_name === schema.class_name;
                return { schema, isTopLevel, existing, isAttached };
              });
              const topLevelAlreadyAttached = schemaStatuses[schemaStatuses.length - 1]?.isAttached;
              return (
                <div className="mb-3 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 p-3" data-testid="schema-preview">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                      Suggested Output Schema{suggestedSchemas.length > 1 ? 's' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowSchemaPreview(false)}
                      className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      data-testid="dismiss-schema"
                    >
                      Dismiss
                    </button>
                  </div>
                  <div className="space-y-2 mb-2">
                    {schemaStatuses.map(({ schema, isTopLevel, existing, isAttached }) => (
                      <div key={schema.class_name} className="text-sm text-gray-800 dark:text-gray-200 font-mono">
                        <span className="font-semibold">{schema.class_name}</span>
                        {isTopLevel && suggestedSchemas.length > 1 && (
                          <span className="text-blue-500 text-xs ml-1">(output)</span>
                        )}
                        {isAttached && (
                          <span className="text-xs text-green-600 dark:text-green-400 ml-2">(already attached)</span>
                        )}
                        {!isAttached && existing && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(will be reused)</span>
                        )}
                        {schema.description && (
                          <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">{schema.description}</span>
                        )}
                        <div className="mt-1 text-xs space-y-0.5 ml-3">
                          {Object.entries(schema.properties).map(([key, type]) => (
                            <div key={key} className="text-gray-600 dark:text-gray-400">
                              {key}: <span className="text-blue-600 dark:text-blue-400">{type}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {onCreateSchema && (
                    <button
                      type="button"
                      onClick={() => onCreateSchema(suggestedSchemas)}
                      disabled={topLevelAlreadyAttached}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
                      data-testid="create-schema-button"
                    >
                      <Check className="w-3 h-3" />
                      {topLevelAlreadyAttached
                        ? 'Schema Already Attached'
                        : `Create & Attach ${suggestedSchemas.length > 1 ? `${suggestedSchemas.length} Schemas` : 'Schema'}`}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Suggested tools preview (AC-1201) */}
            {suggestedTools && suggestedTools.length > 0 && showToolsPreview && (() => {
              const TOOL_META: Record<string, { label: string; icon: React.ElementType }> = {
                web_search: { label: 'Web Search', icon: Globe },
                knowledge_search: { label: 'Knowledge Base Search', icon: BookOpen },
              };
              const newTools = suggestedTools.filter(t => !attachedToolTypes.includes(t.type));
              const allAlreadyAttached = newTools.length === 0;
              return (
                <div className="mb-3 border border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 p-3" data-testid="tools-preview">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                      Suggested Tool{suggestedTools.length > 1 ? 's' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowToolsPreview(false)}
                      className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      data-testid="dismiss-tools"
                    >
                      Dismiss
                    </button>
                  </div>
                  <div className="space-y-2 mb-2">
                    {suggestedTools.map((tool) => {
                      const meta = TOOL_META[tool.type] || { label: tool.type, icon: Sparkles };
                      const Icon = meta.icon;
                      const isAttached = attachedToolTypes.includes(tool.type);
                      return (
                        <div key={tool.type} className="flex items-start gap-2 text-sm">
                          <Icon className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                          <div className="flex-1">
                            <span className="font-medium text-gray-800 dark:text-gray-200">{meta.label}</span>
                            {isAttached && (
                              <span className="text-xs text-green-600 dark:text-green-400 ml-2">(already attached)</span>
                            )}
                            {tool.rationale && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">{tool.rationale}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {onAttachTools && (
                    <button
                      type="button"
                      onClick={() => onAttachTools(newTools)}
                      disabled={allAlreadyAttached}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
                      data-testid="attach-tools-button"
                    >
                      <Check className="w-3 h-3" />
                      {allAlreadyAttached
                        ? 'All Tools Already Attached'
                        : `Attach ${newTools.length > 1 ? `${newTools.length} Tools` : 'Tool'}`}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Conversation history */}
            {conversation.length > 0 && (
              <div className="mb-3 max-h-32 overflow-y-auto space-y-2">
                {conversation.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`text-xs px-2 py-1 rounded ${
                      msg.role === 'user'
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 ml-8'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 mr-8'
                    }`}
                  >
                    <span className="font-medium">{msg.role === 'user' ? 'You' : 'AI'}:</span>{' '}
                    {msg.content.length > 200 ? msg.content.substring(0, 200) + '...' : msg.content}
                  </div>
                ))}
                <div ref={conversationEndRef} />
              </div>
            )}

            {/* Refinement input */}
            <div className="flex gap-2 mb-3">
              <input
                ref={inputRef}
                type="text"
                value={refinementInput}
                onChange={(e) => setRefinementInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Refine: e.g., 'make it more concise' or 'add error handling'"
                disabled={isLoading}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                data-testid="refinement-input"
              />
              <button
                type="button"
                onClick={handleRefine}
                disabled={isLoading || !refinementInput.trim()}
                className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="send-refinement"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>

            {/* Accept / Reject buttons */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                data-testid="reject-button"
              >
                <RotateCcw className="w-3 h-3" />
                Reject
              </button>
              <button
                type="button"
                onClick={handleAccept}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                data-testid="accept-button"
              >
                <Check className="w-3 h-3" />
                Accept
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InstructionAssistantPanel;
